//#region imports
import React, { useMemo, useState } from 'react';
//#endregion

//#region styles
const panelStyle = {
    border: '1px solid #fde68a',
    backgroundColor: '#fffbeb',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '24px',
};

const headerRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
};

const titleStyle = {
    margin: 0,
    fontSize: '15px',
    fontWeight: 600,
    color: '#78350f',
};

const subtitleStyle = {
    fontSize: '13px',
    color: '#92400e',
};

const sectionStyle = {
    marginTop: '12px',
};

const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: '8px 10px',
    backgroundColor: '#fef3c7',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#78350f',
    userSelect: 'none',
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    backgroundColor: '#fff',
    marginTop: '8px',
};

const thStyle = {
    border: '1px solid #e5e7eb',
    padding: '8px 10px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '12px',
    color: '#374151',
};

const tdStyle = {
    border: '1px solid #e5e7eb',
    padding: '8px 10px',
    textAlign: 'left',
    fontSize: '12px',
    color: '#1f2937',
};

const reasonPillStyle = (reason) => {
    const palette = {
        'No matching flight': { bg: '#fee2e2', fg: '#991b1b' },
        'No assistance codes': { bg: '#e0e7ff', fg: '#3730a3' },
        'Matched to flight, no journey or no-selection task': { bg: '#fef3c7', fg: '#78350f' },
        noData: { bg: '#fee2e2', fg: '#991b1b' },
        noOnBlocks: { bg: '#fef3c7', fg: '#78350f' },
        noAtLocation: { bg: '#e0e7ff', fg: '#3730a3' },
        noPassengers: { bg: '#dbeafe', fg: '#1e3a8a' },
        onBlocksInstanceMismatch: { bg: '#ffedd5', fg: '#9a3412' },
    };
    const c = palette[reason] || { bg: '#f3f4f6', fg: '#374151' };
    return {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.fg,
        whiteSpace: 'nowrap',
    };
};
//#endregion

//#region helpers
// Human-readable labels for backend droppedFlights reason codes. Unmapped codes
// fall back to the raw string, matching how the older reasons already render.
const DROPPED_REASON_LABELS = {
    onBlocksInstanceMismatch: 'Wrong flight instance (cross-midnight)',
};
const droppedReasonLabel = (reason) => DROPPED_REASON_LABELS[reason] || reason;

const formatDateTime = (value) => {
    if (!value) return '–';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatRoute = (origin, destination) => {
    if (!origin && !destination) return '–';
    return `${origin || '?'} → ${destination || '?'}`;
};

const formatFlight = (carrierCode, flightNumber) => {
    if (!carrierCode && !flightNumber) return '–';
    return `${carrierCode || ''}${flightNumber || ''}`;
};

// Deduplicate dropped flights by (flightId|flightNumber, reason). The same
// flight is dropped by multiple KPIs every day, so without grouping the user
// sees the same row 6 times with only the kpiKey differing.
const dedupeDroppedFlights = (rows) => {
    const map = new Map();
    rows.forEach((row) => {
        const key = `${row.flightId || row.flightNumber}|${row.reason}|${row.date || ''}`;
        const existing = map.get(key);
        if (existing) {
            if (!existing.kpis.includes(row.kpiKey)) existing.kpis.push(row.kpiKey);
        } else {
            map.set(key, { ...row, kpis: row.kpiKey ? [row.kpiKey] : [] });
        }
    });
    return Array.from(map.values());
};

const dedupeUnevaluatedBookings = (rows) => {
    const map = new Map();
    rows.forEach((row) => {
        const key = `${row.bookingId}|${row.date || ''}`;
        const existing = map.get(key);
        if (existing) {
            if (!existing.kpis.includes(row.kpiKey)) existing.kpis.push(row.kpiKey);
        } else {
            map.set(key, { ...row, kpis: row.kpiKey ? [row.kpiKey] : [] });
        }
    });
    return Array.from(map.values());
};
//#endregion

const CollapsibleSection = ({ title, count, defaultOpen = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={sectionStyle}>
            <div
                style={sectionHeaderStyle}
                onClick={() => setOpen((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen((v) => !v); }}
            >
                <span>{title} ({count})</span>
                <span style={{ fontSize: '14px' }}>{open ? '\u25BE' : '\u25B8'}</span>
            </div>
            {open && children}
        </div>
    );
};

const InboundSlaIgnoredPanel = ({ ignoredBookings = [], dailyReports = [], hideBreakdown = false }) => {

    //#region flatten per-KPI drops out of dailyReports
    const droppedFlights = useMemo(() => {
        const rows = [];
        (dailyReports || []).forEach((report) => {
            const kpis = report?.kpis || {};
            Object.keys(kpis).forEach((kpiKey) => {
                const list = kpis[kpiKey]?.droppedFlights;
                if (!Array.isArray(list)) return;
                list.forEach((d) => rows.push({ ...d, date: report.date, kpiKey }));
            });
        });
        return dedupeDroppedFlights(rows);
    }, [dailyReports]);

    const unevaluatedBookings = useMemo(() => {
        const rows = [];
        (dailyReports || []).forEach((report) => {
            const kpis = report?.kpis || {};
            Object.keys(kpis).forEach((kpiKey) => {
                const list = kpis[kpiKey]?.unevaluatedBookings;
                if (!Array.isArray(list)) return;
                list.forEach((u) => rows.push({ ...u, date: report.date, kpiKey }));
            });
        });
        return dedupeUnevaluatedBookings(rows);
    }, [dailyReports]);
    //#endregion

    //#region ignoredBookings reason-counts
    const ignoredReasonCounts = useMemo(() => {
        const counts = {};
        ignoredBookings.forEach((row) => {
            const r = row.reason || 'Unknown';
            counts[r] = (counts[r] || 0) + 1;
        });
        return counts;
    }, [ignoredBookings]);
    //#endregion

    const totalIssues = ignoredBookings.length + droppedFlights.length + unevaluatedBookings.length;
    if (totalIssues === 0) return null;

    const reasonSummary = Object.entries(ignoredReasonCounts)
        .map(([reason, n]) => `${n} ${reason.toLowerCase()}`)
        .join(' · ');

    return (
        <section style={panelStyle}>
            <div style={headerRowStyle}>
                <div>
                    <h3 style={titleStyle}>Ignored / Unmatched Passengers</h3>
                    <p style={{ ...subtitleStyle, marginTop: 4, marginBottom: 0 }}>
                        {ignoredBookings.length} unmatched booking{ignoredBookings.length === 1 ? '' : 's'}
                        {' · '}
                        {droppedFlights.length} flight{droppedFlights.length === 1 ? '' : 's'} dropped
                        {' · '}
                        {unevaluatedBookings.length} matched but not evaluated
                    </p>
                </div>
            </div>

            {hideBreakdown ? (
                <p style={{ ...subtitleStyle, marginTop: 12, marginBottom: 0 }}>
                    Per-passenger breakdown is hidden for your role.
                </p>
            ) : (
                <>
                    {ignoredBookings.length > 0 && (
                        <CollapsibleSection
                            title="Unmatched passengers (never reached any KPI)"
                            count={ignoredBookings.length}
                            defaultOpen
                        >
                            {reasonSummary && (
                                <p style={{ ...subtitleStyle, margin: '4px 2px 8px' }}>By reason: {reasonSummary}</p>
                            )}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Date</th>
                                            <th style={thStyle}>Booking ID</th>
                                            <th style={thStyle}>Name</th>
                                            <th style={thStyle}>Flight</th>
                                            <th style={thStyle}>Route</th>
                                            <th style={thStyle}>Scheduled Arrival</th>
                                            <th style={thStyle}>From Prev Day</th>
                                            <th style={thStyle}>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ignoredBookings.map((row) => (
                                            <tr key={`${row.date}-${row.bookingId}`}>
                                                <td style={tdStyle}>{row.date || '–'}</td>
                                                <td style={tdStyle}>{row.bookingId || '–'}</td>
                                                <td style={tdStyle}>{row.name || '–'}</td>
                                                <td style={tdStyle}>{formatFlight(row.carrierCode, row.flightNumber)}</td>
                                                <td style={tdStyle}>{formatRoute(row.origin, row.destination)}</td>
                                                <td style={tdStyle}>{formatDateTime(row.arrivalTime)}</td>
                                                <td style={tdStyle}>{row.fromPrevDay ? 'Yes' : 'No'}</td>
                                                <td style={tdStyle}><span style={reasonPillStyle(row.reason)}>{row.reason || '–'}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CollapsibleSection>
                    )}

                    {droppedFlights.length > 0 && (
                        <CollapsibleSection
                            title="Flights dropped by a KPI"
                            count={droppedFlights.length}
                        >
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Date</th>
                                            <th style={thStyle}>Flight</th>
                                            <th style={thStyle}>Reason</th>
                                            <th style={thStyle}>Tasks</th>
                                            <th style={thStyle}>Bookings</th>
                                            <th style={thStyle}>KPIs affected</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {droppedFlights.map((row, idx) => (
                                            <tr key={`${row.date}-${row.flightId || row.flightNumber}-${row.reason}-${idx}`}>
                                                <td style={tdStyle}>{row.date || '–'}</td>
                                                <td style={tdStyle}>{row.flightNumber || '–'}</td>
                                                <td style={tdStyle}><span style={reasonPillStyle(row.reason)}>{droppedReasonLabel(row.reason)}</span></td>
                                                <td style={tdStyle}>{row.taskCount}</td>
                                                <td style={tdStyle}>{row.bookingCount}</td>
                                                <td style={tdStyle}>{(row.kpis || []).join(', ') || '–'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CollapsibleSection>
                    )}

                    {unevaluatedBookings.length > 0 && (
                        <CollapsibleSection
                            title="Matched bookings with no journey signal"
                            count={unevaluatedBookings.length}
                        >
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Date</th>
                                            <th style={thStyle}>Booking ID</th>
                                            <th style={thStyle}>Name</th>
                                            <th style={thStyle}>Flight</th>
                                            <th style={thStyle}>Reason</th>
                                            <th style={thStyle}>KPIs affected</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unevaluatedBookings.map((row, idx) => (
                                            <tr key={`${row.date}-${row.bookingId}-${idx}`}>
                                                <td style={tdStyle}>{row.date || '–'}</td>
                                                <td style={tdStyle}>{row.bookingId || '–'}</td>
                                                <td style={tdStyle}>{row.name || '–'}</td>
                                                <td style={tdStyle}>{row.flightNumber || '–'}</td>
                                                <td style={tdStyle}><span style={reasonPillStyle(row.reason)}>{row.reason}</span></td>
                                                <td style={tdStyle}>{(row.kpis || []).join(', ') || '–'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CollapsibleSection>
                    )}
                </>
            )}
        </section>
    );
};

export default InboundSlaIgnoredPanel;
